// @givmo/board — the one way a set of minutes is approved (CBG-RC11, D-CBG-26): the tolerant read of
// the minutes read's `approval_way`, and what the minutes page shows for it.
//
// ══ THE SERVER DECIDES THE WAY; THIS FILE ONLY WORDS IT ═════════════════════════════════════════
//
// The board's rule (Meeting rules) and the minutes' kind decide ONE way, in one pure backend function
// (`approval_way`, backend `board_minutes.md` §A3), and every approval door refuses the others at its
// start (§A4). The minutes read serves the answer (§A6): the way, whether the deciding rule is recorded,
// whether a meeting may approve them now, the next meeting, a motion already recorded, and the
// directors the minutes record as conflicted. This file never re-derives any of it (the orchestrator's
// C2): a second statement of the rule here could disagree with the one the server enforces.
//
// A read carrying no `approval_way` (approved minutes, or a backend before RC11) gives `null`, and the
// page then offers no approval action rather than one it can't tell the board's rule allows.
//
// A committee meeting's minutes (CBG-YR6-APPROVE, frame c5) are approved the committee's own way: the
// read names the committee (`approval_way.committee`) and whether ITS rule is recorded — while it
// isn't, your board's way stands in, and the line says so. The way's words are RC11's with the
// committee in the board's place (`committee-meetings-words.ts`); the server still decides the way.

import type { BoardMinutesOut } from '@givmo/api-client';
import { displayLongDate, todayAsDateInput } from '../../lib/display';
import { oneLine } from '../conflicts/coi-decode';
import { namedSets, type NamedSet } from '../consents/minutes-consent-decode';
import { minutesSetsWords as several } from '../consents/minutes-sets-words';
import { meetingDayOf } from '../in-meeting/in-meeting-decode';
import { minutesRuleWords as words } from './minutes-rule-words';
import { minutesSignaturesWords as rc10 } from './minutes-signatures-words';
import { committeeMeetingsWords as cm } from '../committees/committee-meetings-words';

/** A seat the minutes record as conflicted, by the name the minutes record (never an id). */
export interface ApprovalWaySeat {
  boardMemberId: string;
  name: string | null;
}

/** A meeting the page points at: the next one, or the one a motion already sits in. */
export interface ApprovalWayMeeting {
  id: string;
  title: string | null;
  startsAt: string | null;
  timeZone: string | null;
}

export interface MinutesApprovalWay {
  /**
   * `written_consent`, `next_meeting`, `officer`, `later_meeting` or (CBG-RC10) `directors_present`;
   * a newer value is kept.
   */
  way: string;
  /** Whether the deciding rule is the board's recorded one (else Givmo's starting point). */
  ruleRecorded: boolean;
  /** Whether a motion at a meeting may approve them now (a meeting way, or §A5 after a withdrawal). */
  meetingDoorOpen: boolean;
  nextMeeting: ApprovalWayMeeting | null;
  pendingMotion: ApprovalWayMeeting | null;
  recused: ApprovalWaySeat[];
  steppedOut: ApprovalWaySeat[];
  /**
   * CBG-RC9-MULTI (§M9): while the way is written consent and no consent naming these minutes is out,
   * the other sets of their meeting "Approve by written consent" would name with them now (frame
   * `c1`); empty otherwise, and from a read without the field.
   */
  consentNamesWith: NamedSet[];
  /**
   * CBG-YR6-APPROVE: a committee meeting's minutes — the committee's name, and whether its OWN rule is
   * recorded (else your board's way stands in) — or null for the board's.
   */
  committee: { name: string; ownRuleRecorded: boolean } | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function seats(value: unknown): ApprovalWaySeat[] {
  if (!Array.isArray(value)) return [];
  const out: ApprovalWaySeat[] = [];
  for (const entry of value) {
    if (!isRecord(entry)) continue;
    const id = text(entry.board_member_id);
    if (id === null || out.some((seat) => seat.boardMemberId === id)) continue;
    out.push({ boardMemberId: id, name: text(entry.display_name) });
  }
  return out;
}

/** `approval_way` off the minutes read, or null when the read carried none. Never throws. */
export function minutesApprovalWay(
  minutes: Pick<BoardMinutesOut, 'approval_way'> | null | undefined,
): MinutesApprovalWay | null {
  const raw: unknown = minutes?.approval_way;
  if (!isRecord(raw)) return null;
  const way = text(raw.way);
  if (way === null) return null;
  const next = isRecord(raw.next_meeting) ? raw.next_meeting : null;
  const pending = isRecord(raw.pending_motion) ? raw.pending_motion : null;
  const nextId = text(next?.id);
  const pendingId = text(pending?.meeting_id);
  const committee = isRecord(raw.committee) ? raw.committee : null;
  return {
    way: way.toLowerCase(),
    ruleRecorded: raw.rule_recorded === true,
    meetingDoorOpen: raw.meeting_door_open === true,
    nextMeeting:
      nextId === null
        ? null
        : {
            id: nextId,
            title: text(next?.title),
            startsAt: text(next?.starts_at),
            timeZone: text(next?.time_zone),
          },
    pendingMotion:
      pendingId === null
        ? null
        : {
            id: pendingId,
            title: text(pending?.meeting_title),
            startsAt: text(pending?.meeting_starts_at),
            // The meeting's own zone, so the day is the meeting's calendar day wherever the reader is.
            timeZone: text(pending?.meeting_time_zone),
          },
    recused: seats(raw.recused),
    steppedOut: seats(raw.stepped_out),
    consentNamesWith: namedSets(raw.consent_names_with),
    committee:
      committee === null
        ? null
        : {
            name: text(committee.name) ?? cm.aCommittee,
            ownRuleRecorded: committee.rule_recorded === true,
          },
  };
}

/**
 * A meeting's day as the action says it — "March 11", the year added when it isn't this year's —
 * in the meeting's own zone where the read carries one (the meeting's calendar, CBG-FIX41's rule),
 * else in the reader's, and kept on one line (`oneLine`): it is said on the page only. Null for an
 * unreadable instant.
 */
export function meetingShortDay(
  meeting: Pick<ApprovalWayMeeting, 'startsAt' | 'timeZone'>,
  today: string = todayAsDateInput(),
): string | null {
  const zoned =
    meeting.timeZone !== null
      ? meetingDayOf({ starts_at: meeting.startsAt, time_zone: meeting.timeZone })
      : null;
  const day = zoned?.day ?? localDay(meeting.startsAt);
  if (day === null) return null;
  const [year, month, date] = day.split('-').map(Number);
  if (year === undefined || month === undefined || date === undefined) return null;
  const local = new Date(year, month - 1, date);
  if (Number.isNaN(local.getTime())) return null;
  const sameYear = today.slice(0, 4) === day.slice(0, 4);
  const said = sameYear
    ? local.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
    : displayLongDate(day);
  return said === null ? null : oneLine(said);
}

function localDay(iso: string | null): string | null {
  if (iso === null) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return todayAsDateInput(date.getTime());
}

/** The names a line says, in the minutes' own order; a name the minutes did not record is said so. */
export function seatNames(seatsOf: readonly ApprovalWaySeat[]): string[] {
  return seatsOf.map((seat) => seat.name ?? 'someone whose name isn’t shown');
}

/** One approval action, in the order the page lays them out (the primary last). */
export type ApprovalAction =
  | { kind: 'consent' }
  | { kind: 'meeting'; meeting: ApprovalWayMeeting; day: string }
  | { kind: 'officer' }
  // CBG-RC10: ask the directors who were in the closed session for their signatures (one press).
  | { kind: 'ask' };

/** A line with its detail behind "When and why". */
export interface ExplainedLine {
  text: string;
  why: readonly string[];
}

/** Everything the minutes page shows about approving one set of draft minutes (§A9's table). */
export interface ApprovalPlan {
  actions: ApprovalAction[];
  /** The way's one plain line, or null while a consent naming them is out (RC9's line says so). */
  line: ExplainedLine | null;
  /** §A8: who the minutes record as conflicted, and under written consent that they sign too. */
  recusal: string | null;
  /** §A5: the meeting way after a withdrawn consent, explained. */
  meetingWay: ExplainedLine | null;
  /** A meeting way with no meeting on the calendar to record the motion at: its line, or null. */
  noNextMeeting: string | null;
  /** A motion approving these minutes is already recorded (C6): where, and no second invitation. */
  pendingMotion: { meeting: ApprovalWayMeeting; day: string | null } | null;
}

const NOTHING: ApprovalPlan = {
  actions: [],
  line: null,
  recusal: null,
  meetingWay: null,
  noNextMeeting: null,
  pendingMotion: null,
};

/**
 * What the page shows for draft minutes under their one way. `consentPending` is RC9's "a consent
 * naming these minutes is out for signature" (`minutesConsentLine`), and `signaturesOut` CBG-RC10's
 * request for the signatures of the directors who were there (`approval_signatures`): the way's line
 * and actions then give way to the line that says what is under way. `onAClosedSession` says the page
 * shows a closed session's own minutes: there, when "Approve by written consent" would name the
 * meeting's own minutes with them, the way's line says so (CBG-RC9-MULTI, frame `c1`).
 *
 * Permissions are the page's: each action is shown only to a holder of its own string.
 */
export function approvalPlan(
  way: MinutesApprovalWay | null,
  input: {
    consentPending: boolean;
    signaturesOut?: boolean;
    onAClosedSession?: boolean;
    today?: string;
  },
): ApprovalPlan {
  if (way === null || input.consentPending || input.signaturesOut === true) return NOTHING;
  const today = input.today ?? todayAsDateInput();
  const recused = seatNames(way.recused);
  const steppedOut = seatNames(way.steppedOut);
  const next = way.nextMeeting;
  const nextDay = next === null ? null : meetingShortDay(next, today);
  const pending =
    way.pendingMotion === null
      ? null
      : { meeting: way.pendingMotion, day: meetingShortDay(way.pendingMotion, today) };
  const meetingAction: ApprovalAction[] =
    pending === null && next !== null && nextDay !== null
      ? [{ kind: 'meeting', meeting: next, day: nextDay }]
      : [];
  // CBG-YR6-APPROVE: a committee's minutes, in the committee's words.
  const committee = way.committee;
  const noNextMeetingLine = committee !== null ? cm.noNextMeeting : words.noNextMeeting;

  switch (way.way) {
    case 'written_consent': {
      // §A5: a withdrawn consent and none out — the meeting way is offered beside a new consent.
      const afterWithdrawal = way.meetingDoorOpen;
      // CBG-RC9-MULTI (c1): a closed session's own minutes, approved with the other sets of their
      // meeting by one consent. On the meeting's own minutes the line stays as it is (m1).
      const namesWith =
        input.onAClosedSession === true && way.consentNamesWith.length > 0
          ? way.consentNamesWith
          : null;
      return {
        actions: afterWithdrawal ? [...meetingAction, { kind: 'consent' }] : [{ kind: 'consent' }],
        line:
          committee !== null
            ? {
                text: cm.writtenConsentLine(committee.name),
                why: cm.writtenConsentWhy({
                  recusedNames: [...steppedOut, ...recused],
                  ownRuleRecorded: committee.ownRuleRecorded,
                }),
              }
            : {
                text:
                  namesWith === null
                    ? words.writtenConsentLine
                    : several.approvedWithLine(namesWith),
                why: words.writtenConsentWhy({
                  recusedNames: [...steppedOut, ...recused],
                  ruleRecorded: way.ruleRecorded,
                }),
              },
        recusal: recusalLine(recused, steppedOut, !afterWithdrawal),
        meetingWay: afterWithdrawal
          ? committee !== null
            ? {
                text: cm.withdrawnLine,
                why: cm.withdrawnWhy(pending === null ? nextDay : null),
              }
            : {
                text: words.withdrawnLine,
                why: words.withdrawnWhy({ meeting: pending === null ? nextDay : null }),
              }
          : null,
        noNextMeeting:
          afterWithdrawal && pending === null && meetingAction.length === 0
            ? noNextMeetingLine
            : null,
        // A motion recorded under an earlier rule is a fact about these minutes either way.
        pendingMotion: pending,
      };
    }
    case 'next_meeting':
    case 'later_meeting':
      return {
        actions: meetingAction,
        line:
          way.way === 'next_meeting'
            ? committee !== null
              ? {
                  text: cm.nextMeetingLine(committee.name),
                  why: cm.nextMeetingWhy(committee.ownRuleRecorded),
                }
              : { text: words.nextMeetingLine, why: words.nextMeetingWhy }
            : { text: words.laterMeetingLine, why: words.laterMeetingWhy },
        recusal: recusalLine(recused, steppedOut, false),
        meetingWay: null,
        noNextMeeting: pending === null && meetingAction.length === 0 ? noNextMeetingLine : null,
        pendingMotion: pending,
      };
    case 'officer':
      return {
        actions: [{ kind: 'officer' }],
        line:
          committee !== null
            ? {
                text: cm.officerLine(committee.name),
                why: cm.officerWhy(committee.ownRuleRecorded),
              }
            : { text: words.officerLine, why: words.officerWhy },
        recusal: recusalLine(recused, steppedOut, false),
        meetingWay: null,
        noNextMeeting: null,
        pendingMotion: pending,
      };
    case 'directors_present':
      // CBG-RC10 (backend §S4, §S11): a closed session's own minutes, approved by the signatures of
      // the directors who were in it. One press asks them; nothing more to choose.
      return {
        actions: [{ kind: 'ask' }],
        line:
          committee !== null
            ? { text: cm.closedSignaturesLine(committee.name), why: cm.closedSignaturesWhy }
            : { text: rc10.wayLine, why: rc10.wayWhy(words.approvalRecordNotDecisions) },
        recusal: recusalLine(recused, steppedOut, false),
        meetingWay: null,
        noNextMeeting: null,
        pendingMotion: pending,
      };
    default:
      // A way this build doesn't know: no action and no line, never a guess.
      return NOTHING;
  }
}

/**
 * §A8's line: who stepped out of a closed session (its own minutes only — the server sends none for
 * a meeting's), then who the minutes record as recused, then, under written consent with no meeting
 * way beside it, that they sign the consent too. Null when the minutes record neither.
 */
function recusalLine(
  recused: readonly string[],
  steppedOut: readonly string[],
  signsToo: boolean,
): string | null {
  const parts: string[] = [];
  if (steppedOut.length > 0) parts.push(words.recordedSteppedOut(steppedOut));
  if (recused.length > 0) parts.push(words.recordedAsRecused(recused));
  if (parts.length === 0) return null;
  if (signsToo) parts.push(words.signsTheConsentToo([...steppedOut, ...recused]));
  return parts.join(' ');
}
